import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import InvitationModal, {
  type Invitation,
  type SimpleClient,
  type SimpleJob,
} from "./components/InvitationModal";
import RsvpModal from "./components/RsvpModal";
import ModuleGate from "@/components/base/ModuleGate";
import { useModuleAccess } from "@/hooks/useModuleAccess";
import { queueEmail, invitationCreatedBody, invitationPublishedBody } from "@/lib/queueEmail";

/* ─── Helpers ────────────────────────────────────────────────── */
const THEME_LABELS: Record<string, string> = {
  classic:   "Classic Elegant",
  modern:    "Modern Minimal",
  floral:    "Floral Garden",
  dark:      "Dark Luxury",
  celestial: "Celestial",
};

const EVENT_ICONS: Record<string, string> = {
  Wedding:        "ri-hearts-line",
  "XV Años":      "ri-sparkling-2-line",
  Baptism:        "ri-water-flash-line",
  Graduation:     "ri-graduation-cap-line",
  Birthday:       "ri-cake-2-line",
  "Baby Shower":  "ri-emotion-happy-line",
  Engagement:     "ri-heart-line",
  Anniversary:    "ri-medal-line",
  "Corporate Event": "ri-building-4-line",
  Other:          "ri-star-line",
};

/* ─── Page ───────────────────────────────────────────────────── */
export default function AdminInvitations() {
  const navigate = useNavigate();
  const { hasAccess: moduleActive } = useModuleAccess("digital_invitations");
  const [tenantId, setTenantId]           = useState<string | null>(null);
  const [invitations, setInvitations]     = useState<Invitation[]>([]);
  const [clients, setClients]             = useState<SimpleClient[]>([]);
  const [jobs, setJobs]                   = useState<SimpleJob[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState<string | null>(null);
  const [search, setSearch]               = useState("");
  const [selected, setSelected]           = useState<Invitation | null>(null);
  const [modalOpen, setModalOpen]         = useState(false);
  const [isNew, setIsNew]                 = useState(false);
  const [togglingId, setTogglingId]       = useState<string | null>(null);
  const [rsvpInv, setRsvpInv]             = useState<Invitation | null>(null);
  const [rsvpOpen, setRsvpOpen]           = useState(false);

  const fetchAll = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const [invRes, clientRes, jobRes] = await Promise.all([
        supabase
          .from("digital_invitations")
          .select("*")
          .eq("tenant_id", tid)
          .order("created_at", { ascending: false }),
        supabase
          .from("clients")
          .select("id, full_name, email")
          .eq("tenant_id", tid)
          .eq("status", "active")
          .order("full_name"),
        supabase
          .from("jobs")
          .select("id, title")
          .eq("tenant_id", tid)
          .order("title"),
      ]);
      if (invRes.error) throw new Error(invRes.error.message);
      if (invRes.data)    setInvitations(invRes.data as Invitation[]);
      if (clientRes.data) setClients(clientRes.data as SimpleClient[]);
      if (jobRes.data)    setJobs(jobRes.data as SimpleJob[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load invitations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchAll(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchAll]);

  /* ── Client name lookup ── */
  const clientName = (id: string) =>
    clients.find((c) => c.id === id)?.full_name ?? "—";

  /* ── Filtered list ── */
  const filtered = invitations.filter(
    (inv) =>
      inv.title.toLowerCase().includes(search.toLowerCase()) ||
      clientName(inv.client_id).toLowerCase().includes(search.toLowerCase()) ||
      inv.event_type.toLowerCase().includes(search.toLowerCase()),
  );

  /* ── Handlers ── */
  const handleNew = () => {
    navigate("/admin/invitations/new");
  };

  const handleEdit = (inv: Invitation) => {
    navigate(`/admin/invitations/${inv.id}`);
  };

  const handleTogglePublish = async (inv: Invitation) => {
    setTogglingId(inv.id);
    const { data, error } = await supabase
      .from("digital_invitations")
      .update({
        is_published: !inv.is_published,
        updated_at: new Date().toISOString(),
      })
      .eq("id", inv.id)
      .eq("tenant_id", tenantId!)
      .select()
      .maybeSingle();

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else if (data) {
      setInvitations((prev) =>
        prev.map((i) => (i.id === inv.id ? (data as Invitation) : i)),
      );

      // ── Queue invitation published email ──
      if ((data as Invitation).is_published) {
        const clientRecord = clients.find((c) => c.id === (data as Invitation).client_id);
        if (clientRecord?.email) {
          const publicUrl = `${window.location.origin}/invite/${(data as Invitation).public_slug}`;
          queueEmail({
            tenantId: tenantId!,
            toEmail: clientRecord.email,
            subject: `Your invitation is live — ${(data as Invitation).title}`,
            body: invitationPublishedBody({
              clientName: clientRecord.full_name,
              invitationTitle: (data as Invitation).title,
              eventType: (data as Invitation).event_type,
              eventDate: (data as Invitation).event_date
                ? new Date((data as Invitation).event_date!).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })
                : undefined,
              publicUrl,
            }),
            templateType: "invitation_published",
            entityType: "digital_invitation",
            entityId: (data as Invitation).id,
            priority: 2,
            metadata: {
              invitation_title: (data as Invitation).title,
              event_type: (data as Invitation).event_type,
              public_slug: (data as Invitation).public_slug,
              public_url: publicUrl,
              client_name: clientRecord.full_name,
            },
          });
        }
      }

      Swal.fire({
        title: (data as Invitation).is_published ? "Published!" : "Unpublished",
        text: (data as Invitation).is_published
          ? "Invitation is now live at /invite/" + inv.public_slug
          : "Invitation is now private.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
        background: "#ffffff",
        color: "#0f172a",
      });
    }
    setTogglingId(null);
  };

  const handleDelete = async (inv: Invitation) => {
    const result = await Swal.fire({
      title: "Delete Invitation?",
      text: `"${inv.title}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      background: "#ffffff",
      color: "#0f172a",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed || !tenantId) return;

    const { error } = await supabase
      .from("digital_invitations")
      .delete()
      .eq("id", inv.id)
      .eq("tenant_id", tenantId);

    if (error) {
      Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
    } else {
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
      Swal.fire({ title: "Deleted", text: "Invitation removed.", icon: "success", timer: 1500, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
    }
  };

  const handleSave = async (data: Invitation) => {
    if (!tenantId) return;

    if (isNew) {
      const { id: _id, created_at: _ca, updated_at: _ua, ...rest } = data;
      const payload = {
        ...rest,
        tenant_id:         tenantId,
        job_id:            rest.job_id || null,
        event_title:       rest.event_title || null,
        host_names:        rest.host_names || null,
        event_date:        rest.event_date || null,
        event_time:        rest.event_time || null,
        venue_name:        rest.venue_name || null,
        venue_address:     rest.venue_address || null,
        google_maps_url:   rest.google_maps_url || null,
        dress_code:        rest.dress_code || null,
        gift_registry_url: rest.gift_registry_url || null,
        cover_image_url:   rest.cover_image_url || null,
        music_url:         rest.music_url || null,
        gallery_urls:      rest.gallery_urls ?? [],
        is_published:      false,
      };
      const { data: created, error } = await supabase
        .from("digital_invitations")
        .insert(payload)
        .select()
        .maybeSingle();

      if (error) {
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
      if (created) {
        setInvitations((prev) => [created as Invitation, ...prev]);
        const clientRecord = clients.find((c) => c.id === (created as Invitation).client_id);
        if (clientRecord?.email) {
          queueEmail({
            tenantId,
            toEmail: clientRecord.email,
            subject: `Your Invitation is Ready - ${(created as Invitation).title}`,
            body: invitationCreatedBody({
              clientName:      clientRecord.full_name,
              invitationTitle: (created as Invitation).title,
              eventType:       (created as Invitation).event_type,
              eventDate:       (created as Invitation).event_date ?? '',
              publicSlug:      (created as Invitation).public_slug,
            }),
          });
        }
      }
      Swal.fire({ title: "Created!", text: "Invitation created. Publish it when ready.", icon: "success", timer: 1800, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
    } else {
      const { data: updated, error } = await supabase
        .from("digital_invitations")
        .update({
          client_id:       data.client_id,
          job_id:          data.job_id || null,
          title:             data.title,
          event_title:       data.event_title || null,
          host_names:        data.host_names || null,
          event_type:        data.event_type,
          event_date:        data.event_date || null,
          event_time:        data.event_time || null,
          venue_name:        data.venue_name || null,
          venue_address:     data.venue_address || null,
          google_maps_url:   data.google_maps_url || null,
          dress_code:        data.dress_code || null,
          gift_registry_url: data.gift_registry_url || null,
          cover_image_url:   data.cover_image_url || null,
          music_url:         data.music_url || null,
          gallery_urls:      data.gallery_urls ?? [],
          theme_key:         data.theme_key,
          public_slug:       data.public_slug,
          rsvp_enabled:      data.rsvp_enabled,
          updated_at:        new Date().toISOString(),
        })
        .eq("id", data.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();

      if (error) {
        Swal.fire({ title: "Error", text: error.message, icon: "error", background: "#ffffff", color: "#0f172a" });
        return;
      }
      if (updated) setInvitations((prev) => prev.map((i) => (i.id === (updated as Invitation).id ? (updated as Invitation) : i)));
      Swal.fire({ title: "Saved", text: "Invitation updated.", icon: "success", timer: 1500, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
    }
    setModalOpen(false);
  };

  const copyLink = (slug: string) => {
    const url = `${window.location.origin}/invite/${slug}`;
    navigator.clipboard.writeText(url);
    Swal.fire({ title: "Copied!", text: url, icon: "success", timer: 1500, showConfirmButton: false, background: "#ffffff", color: "#0f172a" });
  };

  return (
    <AdminLayout>
      <AdminHeader
        title="Digital Invitations"
        subtitle="Create and manage event invitations for your clients"
        actions={
          moduleActive ? (
            <button
              onClick={handleNew}
              className="flex items-center gap-2 bg-[#2563eb] hover:bg-blue-700 text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </span>
              New Invitation
            </button>
          ) : undefined
        }
      />

      <ModuleGate moduleId="digital_invitations">
      <div className="px-4 md:px-8 py-4 md:py-7">
        {/* Search */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search invitations, clients, events…"
              className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#2563eb]/50 transition-colors"
            />
          </div>
          <span className="text-slate-400 text-sm ml-auto">{filtered.length} results</span>
        </div>

        {/* Cards grid */}
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={() => tenantId && fetchAll(tenantId)} showLogin />
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 max-w-xs mx-auto text-center gap-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-blue-50">
              <i className="ri-mail-open-line text-[#2563eb] text-3xl"></i>
            </div>
            <div>
              <h3 className="text-[#0f172a] font-semibold text-base mb-1.5">
                {invitations.length === 0 ? "No invitations yet" : "No invitations found"}
              </h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {invitations.length === 0
                  ? "Create beautiful digital invitations for your clients\u2019 special events."
                  : "Try adjusting your search term."}
              </p>
            </div>
            {invitations.length === 0 && (
              <button
                onClick={handleNew}
                className="flex items-center gap-2 bg-[#2563eb] hover:bg-blue-700 text-black text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                Create First Invitation
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filtered.map((inv) => {
              const isToggling = togglingId === inv.id;
              const icon = EVENT_ICONS[inv.event_type] ?? "ri-star-line";

              return (
                <div
                  key={inv.id}
                  className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col group"
                >
                  {/* Cover */}
                  <div className="relative h-36 bg-gradient-to-br from-slate-700 to-slate-900 overflow-hidden">
                    {inv.cover_image_url ? (
                      <img
                        src={inv.cover_image_url}
                        alt={inv.title}
                        className="w-full h-full object-cover object-top opacity-60"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-14 h-14 flex items-center justify-center text-blue-300/50">
                          <i className={`${icon} text-4xl`}></i>
                        </div>
                      </div>
                    )}
                    {/* Overlay gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />

                    {/* Status badge */}
                    <div className="absolute top-3 right-3">
                      <span
                        className={`text-[10px] px-2.5 py-1 rounded-full font-semibold ${
                          inv.is_published
                            ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200"
                            : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        {inv.is_published ? "Live" : "Draft"}
                      </span>
                    </div>

                    {/* Theme badge */}
                    <div className="absolute top-3 left-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-900/40 text-slate-400">
                        {THEME_LABELS[inv.theme_key] ?? inv.theme_key}
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="p-4 flex-1 flex flex-col gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-3.5 h-3.5 flex items-center justify-center text-[#2563eb]/60">
                          <i className={`${icon} text-xs`}></i>
                        </span>
                        <span className="text-slate-400 text-xs">{inv.event_type}</span>
                      </div>
                      <p className="text-[#0f172a] font-semibold text-sm leading-tight">{inv.title}</p>
                      <p className="text-slate-400 text-xs mt-0.5">{clientName(inv.client_id)}</p>
                    </div>

                    {/* Details */}
                    <div className="space-y-1.5 text-xs text-slate-400">
                      {inv.event_date && (
                        <div className="flex items-center gap-1.5">
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-calendar-2-line text-[11px]"></i>
                          </span>
                          {new Date(inv.event_date).toLocaleDateString("en-US", {
                            weekday: "short", month: "short", day: "numeric", year: "numeric",
                          })}
                        </div>
                      )}
                      {inv.venue_name && (
                        <div className="flex items-center gap-1.5">
                          <span className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-map-pin-line text-[11px]"></i>
                          </span>
                          <span className="truncate">{inv.venue_name}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <span className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-link-m text-[11px]"></i>
                        </span>
                        <span className="font-mono truncate text-[10px]">/invite/{inv.public_slug}</span>
                      </div>
                    </div>

                    {/* RSVP badge */}
                    {inv.rsvp_enabled && (
                      <span className="self-start text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-[#2563eb]">
                        RSVP enabled
                      </span>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-1 mt-auto border-t border-slate-200">
                      {/* RSVPs button */}
                      {inv.rsvp_enabled && (
                        <button
                          onClick={() => { setRsvpInv(inv); setRsvpOpen(true); }}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#2563eb] hover:bg-blue-50 rounded-lg transition-all cursor-pointer"
                          title="View RSVPs"
                        >
                          <i className="ri-group-line text-sm"></i>
                        </button>
                      )}
                      {/* Publish toggle */}
                      <button
                        onClick={() => handleTogglePublish(inv)}
                        disabled={isToggling}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap ${
                          inv.is_published
                            ? "bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-500"
                            : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                        }`}
                      >
                        {isToggling ? (
                          <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <span className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className={inv.is_published ? "ri-eye-off-line" : "ri-send-plane-line"}></i>
                            </span>
                            {inv.is_published ? "Unpublish" : "Publish"}
                          </>
                        )}
                      </button>

                      {/* Copy link */}
                      {inv.is_published && (
                        <button
                          onClick={() => copyLink(inv.public_slug)}
                          className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#2563eb] hover:bg-blue-50 rounded-lg transition-all cursor-pointer"
                          title="Copy public link"
                        >
                          <i className="ri-links-line text-sm"></i>
                        </button>
                      )}

                      {/* Edit */}
                      <Link
                        to={`/admin/invitations/${inv.id}`}
                        className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                        title="Edit"
                      >
                        <i className="ri-pencil-line text-sm"></i>
                      </Link>

                      {/* Delete */}
                      <button
                        onClick={() => handleDelete(inv)}
                        className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-400/10 rounded-lg transition-all cursor-pointer"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {rsvpOpen && rsvpInv && tenantId && (
        <RsvpModal
          invitation={rsvpInv}
          tenantId={tenantId}
          onClose={() => { setRsvpOpen(false); setRsvpInv(null); }}
        />
      )}
      {modalOpen && (
        <InvitationModal
          invitation={selected}
          isNew={isNew}
          clients={clients}
          jobs={jobs}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
      </ModuleGate>
    </AdminLayout>
  );
}
